import Mail from 'assets/svg/icons/Mail';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TracingConfig } from 'utils/amplitude/amplitude.types';

type ExpertEmailFieldWithSuggestionsProps = {
  name?: string;
  label?: string;
  placeholder?: string;
  storedEmails: string[];
  tracing?: TracingConfig;
  maxLength?: number;
};

const ExpertEmailFieldWithSuggestions: FC<
  ExpertEmailFieldWithSuggestionsProps
> = ({
  name = 'email',
  label,
  placeholder,
  storedEmails,
  tracing,
  maxLength = 100,
}) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();
  const emailValue = watch(name);

  const filteredEmails =
    emailValue && emailValue.length > 0
      ? storedEmails.filter((email) =>
          email.toLowerCase().startsWith(emailValue.toLowerCase()),
        )
      : [];

  const shouldShowSuggestions =
    filteredEmails.length > 0 &&
    !storedEmails.some(
      (email) => email.toLowerCase() === emailValue?.toLowerCase(),
    );

  return (
    <FieldConnector label={label ?? t('login.email')} name={name}>
      {({ field }) => (
        <div className="relative">
          <FreeTextInput
            {...field}
            Icon={Mail}
            iconClassName="p-0.5"
            maxLength={maxLength}
            placeholder={placeholder ?? t('login.emailPlaceholder')}
            tracing={tracing}
          />

          {shouldShowSuggestions && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredEmails.map((email) => (
                <div
                  key={email}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors text-gray-700"
                  onClick={() =>
                    setValue(name, email, { shouldValidate: true })
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setValue(name, email, { shouldValidate: true });
                    }
                  }}
                  role="option"
                  tabIndex={0}
                >
                  {email}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </FieldConnector>
  );
};

export default ExpertEmailFieldWithSuggestions;
